import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Settings, Calendar, Mail, FileText, FileSpreadsheet,
  Save, Send, Trash2, Plus, X, Check, AlertCircle,
  RefreshCw, Clock, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportConfigurationPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Configuration state
  const [config, setConfig] = useState({
    is_enabled: false,
    day_of_month: 1,
    recipient_emails: [],
    report_format: 'pdf'
  });
  
  // New email input
  const [newEmail, setNewEmail] = useState('');
  
  // SEC-003 — sentinelle (auth réelle via cookie HttpOnly + axios withCredentials)
  const getToken = () => 'cookie-auth';
  
  // Fetch configuration on mount
  useEffect(() => {
    const fetchConfig = async () => {
      const token = getToken();
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/report-config/my-config`, {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        });
        
        if (response.ok) {
          const data = await response.json();
          setConfig({
            is_enabled: data.is_enabled || false,
            day_of_month: data.day_of_month || 1,
            recipient_emails: data.recipient_emails || [],
            report_format: data.report_format || 'pdf'
          });
        }
      } catch (error) {
        console.error('Error fetching config:', error);
        toast.error('Erreur lors du chargement de la configuration');
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfig();
  }, []);
  
  // Save configuration
  const handleSave = async () => {
    const token = getToken();
    if (!token) return;
    
    if (config.is_enabled && config.recipient_emails.length === 0) {
      toast.error('Veuillez ajouter au moins un destinataire');
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/report-config/my-config`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer cookie-auth',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Configuration enregistrée avec succès');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  // Send test report
  const handleSendTest = async () => {
    const token = getToken();
    if (!token) return;
    
    if (config.recipient_emails.length === 0) {
      toast.error('Veuillez ajouter au moins un destinataire');
      return;
    }
    
    // Save first
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/report-config/my-config`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer cookie-auth',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
    } catch (e) {
      console.error('Error saving before test:', e);
    }
    setSaving(false);
    
    setSendingTest(true);
    
    try {
      const response = await fetch(`${API_URL}/api/report-config/test-send`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer cookie-auth',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Rapport de test envoyé');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi du test');
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error('Erreur lors de l\'envoi du test');
    } finally {
      setSendingTest(false);
    }
  };
  
  // Add email
  const handleAddEmail = () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@')) {
      toast.error('Veuillez saisir une adresse email valide');
      return;
    }
    
    if (config.recipient_emails.includes(email)) {
      toast.error('Cette adresse est déjà dans la liste');
      return;
    }
    
    setConfig(prev => ({
      ...prev,
      recipient_emails: [...prev.recipient_emails, email]
    }));
    setNewEmail('');
  };
  
  // Remove email
  const handleRemoveEmail = (emailToRemove) => {
    setConfig(prev => ({
      ...prev,
      recipient_emails: prev.recipient_emails.filter(e => e !== emailToRemove)
    }));
  };
  
  // Generate days array
  const days = Array.from({ length: 28 }, (_, i) => i + 1);
  
  const formatOrdinal = (n) => {
    if (n === 1) return '1er';
    return `${n}`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="report-config-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-emerald-100">
          <Settings className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Configuration des rapports</h1>
          <p className="text-gray-500">Recevez automatiquement vos rapports mensuels par email</p>
        </div>
      </div>
      
      {/* Main Card */}
      <Card className="mb-6">
        <CardHeader className="border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Rapports automatiques mensuels</CardTitle>
              <CardDescription>
                Configurez l'envoi automatique de vos rapports de performance
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${config.is_enabled ? 'text-emerald-600' : 'text-gray-500'}`}>
                {config.is_enabled ? 'Activé' : 'Désactivé'}
              </span>
              <Switch
                checked={config.is_enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, is_enabled: checked }))}
                data-testid="enable-switch"
              />
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6 space-y-6">
          {/* Schedule */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              Jour d'envoi
            </Label>
            <div className="flex items-center gap-3">
              <Select
                value={String(config.day_of_month)}
                onValueChange={(value) => setConfig(prev => ({ ...prev, day_of_month: parseInt(value) }))}
              >
                <SelectTrigger className="w-[180px]" data-testid="day-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {days.map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      {formatOrdinal(day)} du mois
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">
                Le rapport du mois précédent sera envoyé chaque {formatOrdinal(config.day_of_month)} du mois
              </span>
            </div>
          </div>
          
          {/* Format */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              Format du rapport
            </Label>
            <Select
              value={config.report_format}
              onValueChange={(value) => setConfig(prev => ({ ...prev, report_format: value }))}
            >
              <SelectTrigger className="w-[250px]" data-testid="format-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-red-500" />
                    PDF uniquement
                  </div>
                </SelectItem>
                <SelectItem value="csv">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-green-500" />
                    CSV uniquement
                  </div>
                </SelectItem>
                <SelectItem value="both">
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      <FileText className="w-4 h-4 text-red-500" />
                      <FileSpreadsheet className="w-4 h-4 text-green-500 -ml-1" />
                    </div>
                    PDF + CSV
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Recipients */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              Destinataires
            </Label>
            
            {/* Email list */}
            {config.recipient_emails.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {config.recipient_emails.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="px-3 py-1.5 text-sm bg-gray-100"
                  >
                    {email}
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-2 hover:text-red-500"
                      data-testid={`remove-email-${email}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Add email input */}
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Ajouter une adresse email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                className="max-w-sm"
                data-testid="new-email-input"
              />
              <Button
                variant="outline"
                onClick={handleAddEmail}
                data-testid="add-email-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </div>
            
            {config.recipient_emails.length === 0 && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Ajoutez au moins un destinataire pour activer l'envoi automatique
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Preview Card */}
      {config.is_enabled && config.recipient_emails.length > 0 && (
        <Card className="mb-6 border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Prochain envoi programmé</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Le rapport sera envoyé le <strong>{formatOrdinal(config.day_of_month)}</strong> du mois prochain à :
                </p>
                <ul className="mt-2 space-y-1">
                  {config.recipient_emails.map((email) => (
                    <li key={email} className="text-sm text-gray-700 flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-emerald-500" />
                      {email}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleSendTest}
          disabled={sendingTest || config.recipient_emails.length === 0}
          data-testid="test-send-btn"
        >
          {sendingTest ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Envoyer un rapport test
            </>
          )}
        </Button>
        
        <Button
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-config-btn"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>
      
      {/* Info */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Comment ça fonctionne ?</h4>
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Chaque mois, à la date choisie, un rapport de performance est généré automatiquement</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Le rapport contient les données du mois précédent : opérations, transferts, KPIs</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Vous pouvez ajouter plusieurs destinataires pour partager le rapport avec votre équipe</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Utilisez "Envoyer un rapport test" pour vérifier que tout fonctionne correctement</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
